import '../styles/Home.css';
import { Link, useNavigate } from "react-router-dom";
import { createProductUrl } from '../utils/paths';
import { useState, useEffect } from "react";
import { RiArrowRightCircleFill, RiPlayCircleFill, RiSparkling2Line } from 'react-icons/ri';
import { useApiCall } from '../hooks/useApiCall';
import { useAccount } from '../contexts/AccountContext';
import { useCatalogType } from '../contexts/CatalogTypeContext';

export default function Home() {
  const navigate = useNavigate();
  const apiCall = useApiCall();

  const [banners, setBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(true);
  const [metrics, setMetrics] = useState({ new_arrivals_count: 0, new_arrivals_preview: [] });
  const [showroomProduct, setShowroomProduct] = useState(null);
  const { account } = useAccount();
  const { catalogType } = useCatalogType();

  // Fetch banners
  useEffect(() => {
    const fetchBanners = async () => {
      setBannersLoading(true);
      try {
        const response = await apiCall('home/banners', 'POST', { type: catalogType });
        if (response.success && response.data.banners) {
          setBanners(response.data.banners);
        }
      } catch (error) {
        console.error('Error fetching banners:', error);
      } finally {
        setBannersLoading(false);
      }
    };
    fetchBanners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogType]);

  // Fetch a random product for the showroom minicard
  useEffect(() => {
    apiCall('home/showroom-product', 'POST', { type: catalogType }).then((response) => {
      if (response?.success) setShowroomProduct(response.data.product);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogType]);

  // Fetch home metrics separately (new arrivals count + last 3 preview items)
  useEffect(() => {
    apiCall('home/metrics', 'POST', { type: catalogType }).then((response) => {
      if (response?.success) {
        setMetrics(response.data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogType]);

  // Create URL-friendly slug from category name
  const createSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s&]/g, '')
      .replace(/\s*&\s*/g, '-and-')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const getBannerUrl = (banner) => {
    // Check if this is a "New Arrivals" banner (category_id = 0 or is_new_arrivals flag)
    if (banner.is_new_arrivals || banner.category_id === 0) {
      return '/products?sort=new';
    } else if (banner.category_id && banner.category_name) {
      const slug = createSlug(banner.category_name);
      return `/products/category/${banner.category_id}/${slug}`;
    }

    return '/products';
  };

  // Split banners into two groups - insert New Arrivals after 3rd banner
  const firstBanners = banners.slice(0, 3);
  const remainingBanners = banners.slice(3);

  return (
    <div className="home-page">
      
      {bannersLoading ? (
        <div className="home-loading">
          <p>Loading...</p>
        </div>
      ) : (
        <>
          {/* First 3 banners */}
          {firstBanners.map((banner) => {
            const title = banner.title || banner.category_name || 'Explore Our Products';
            const subtitle = banner.subtitle || '';
            const cta = `Explore ${banner.category_name || 'Products'}`;
            
            return (
              <Link 
                key={banner.id} 
                className="home-banner"
                to={getBannerUrl(banner)}
                style={{ 
                  backgroundImage: banner.image ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${banner.image})` : '',
                }}
              >
                <div className="home-banner-content">
                  <h2 className="home-banner-title">{title}</h2>
                  {subtitle && <p className="home-banner-subtitle">{subtitle}</p>}
                  <span className="home-banner-cta">
                    {cta}
                    <RiArrowRightCircleFill className="home-banner-arrow" size={24} />
                  </span>
                </div>
              </Link>
            );
          })}

          {/* New Arrivals Banner - only visible when new_arrival products exist */}
          {metrics.new_arrivals_count > 0 && (
            <div
              className="home-banner home-banner-new-arrivals"
              onClick={() => navigate('/products?sort=new')}
            >
              <div className="home-na-left">
                <span className="home-banner-badge">NEW</span>
                <h2 className="home-banner-title">New Arrivals</h2>
                <p className="home-banner-subtitle">
                  {metrics.new_arrivals_count} new product{metrics.new_arrivals_count !== 1 ? 's' : ''} just landed
                </p>
                <Link
                  className="home-banner-cta"
                  to="/products?sort=new"
                  onClick={(e) => e.stopPropagation()}
                >
                  Shop New Arrivals
                  <RiArrowRightCircleFill className="home-banner-arrow" size={24} />
                </Link>
              </div>
              <div className="home-na-products">
                {metrics.new_arrivals_preview.map((product) => (
                  <Link
                    key={product.id}
                    className="home-na-card"
                    to={createProductUrl(product.id, product.name)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {product.image
                      ? <img src={product.image} alt={product.name} className="home-na-card-image" />
                      : <div className="home-na-card-image home-na-card-no-image" />}
                    <div className="home-na-card-info">
                      <div className="home-na-card-name">{product.name}</div>
                      <div className="home-na-card-price">
                        {product.coming_soon
                          ? <span className="home-na-coming-soon">Coming Soon</span>
                          : `${product.price.curr} ${product.price.amount}`
                        }
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Remaining banners */}
          {remainingBanners.map((banner) => {
            const title = banner.title || banner.category_name || 'Explore Our Products';
            const subtitle = banner.subtitle || '';
            const cta = `Explore ${banner.category_name || 'Products'}`;
            
            return (
              <Link 
                key={banner.id} 
                className="home-banner"
                to={getBannerUrl(banner)}
                style={{ 
                  backgroundImage: banner.image ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${banner.image})` : '',
                }}
              >
                <div className="home-banner-content">
                  <h2 className="home-banner-title">{title}</h2>
                  {subtitle && <p className="home-banner-subtitle">{subtitle}</p>}
                  <span className="home-banner-cta">
                    {cta}
                    <RiArrowRightCircleFill className="home-banner-arrow" size={24} />
                  </span>
                </div>
              </Link>
            );
          })}

          {/* Virtual Showroom Banner */}
          { (account?.admin) &&
          <div className="home-showroom-banner" onClick={() => navigate('/experience')}>
            <div className="home-showroom-bg" />
            <div className="home-showroom-content">
              <span className="home-showroom-badge">IMMERSIVE EXPERIENCE</span>
              <h2 className="home-showroom-title">Virtual<br/>Showroom</h2>
              <p className="home-showroom-subtitle">Step inside our curated collection. Explore room setups, discover products and shop directly from the scene.</p>
              <button className="home-showroom-cta">
                <RiPlayCircleFill size={20} />
                Enter Showroom
              </button>
            </div>

            {/* Right side: real video preview window */}
            <div className="home-showroom-visual">
              <div className="home-showroom-frame">
                {/* Top chrome bar */}
                <div className="home-showroom-frame-chrome">
                  <div className="home-showroom-chrome-dots">
                    <span className="chrome-dot-close" />
                    <span className="chrome-dot-min" />
                    <span className="chrome-dot-max" />
                  </div>
                  <span className="home-showroom-chrome-label">BDS Virtual Showroom</span>
                  <RiSparkling2Line size={11} style={{color:'var(--catalog-primary)', flexShrink:0, opacity:0.8}} />
                </div>
                {/* Animated dot hotspots */}
                <div className="home-showroom-dot" style={{left:'28%', top:'52%'}}>
                  <div className="home-showroom-dot-inner" />
                </div>
                <div className="home-showroom-dot home-showroom-dot--active" style={{left:'58%', top:'38%'}}>
                  <div className="home-showroom-dot-inner" />
                  {showroomProduct && (
                    <div className="home-showroom-minicard">
                      <div className="home-showroom-minicard-img">
                        <img src={showroomProduct.image} alt={showroomProduct.name} />
                      </div>
                      <div className="home-showroom-minicard-body">
                        <div className="home-showroom-minicard-name">{showroomProduct.name}</div>
                        <div className="home-showroom-minicard-price">{showroomProduct.price.curr} {showroomProduct.price.amount}</div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="home-showroom-dot" style={{left:'76%', top:'64%'}}>
                  <div className="home-showroom-dot-inner" />
                </div>
                {/* Chapter steps */}
                <div className="home-showroom-screen-nav">
                  <div className="home-showroom-screen-nav-step active" />
                  <div className="home-showroom-screen-nav-step" />
                  <div className="home-showroom-screen-nav-step" />
                  <div className="home-showroom-screen-nav-step" />
                </div>
              </div>
              {/* Bottom reflection */}
              <div className="home-showroom-reflection" />
            </div>

            <div className="home-showroom-decoration">
              <div className="home-showroom-ring home-showroom-ring-1" />
              <div className="home-showroom-ring home-showroom-ring-2" />
              <div className="home-showroom-ring home-showroom-ring-3" />
            </div>
          </div>
          }
        </>
      )}

    </div>
  );
}
